import NavItem from "./NavItem";
import GatewayList from "../GatewayList";
import { useNavigation } from "../../context/NavigationContext";
import { 
  HomeIcon, 
  RocketLaunchIcon,
  BeakerIcon,
  ArrowTopRightOnSquareIcon,
  CloudIcon,
  WrenchScrewdriverIcon,
  MapPinIcon,
  BellIcon,
  SparklesIcon,
} from "@heroicons/react/24/solid";
import { FaDiscord, FaBook, FaMobileAlt } from 'react-icons/fa';
import { useState, useEffect, useMemo } from "react";
import { useMatchRoute } from "@tanstack/react-router";
import { useGateways } from "../../context/GatewayContext";
import { InformationCircleIcon, ChatBubbleLeftRightIcon, KeyIcon } from "@heroicons/react/24/outline";
import { MeterIcon, SolarIcon, BatteryIcon } from '../icons/icons';
import WalletConnect from "../wallet/WalletConnect";
import { isFeatureEnabled } from "../../config/featureFlags";
import { BoltIcon } from '@heroicons/react/24/solid';
import PlusCpuIcon from '../icons/PlusCpuIcon';
import { useWalletContext } from "../../context/WalletContext";
import { WalletTypeDetector } from "../../wallets/WalletTypeDetector";
import CrownIcon from '../icons/CrownIcon';

// Globe Icon Component for the Explorer link
const GlobeIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 2a14.5 14.5 0 0 0 0 20a14.5 14.5 0 0 0 0-20"/>
    <path d="M2 12h20"/>
  </svg>
);

// Life Buoy Icon Component for the Support link
const LifeBuoyIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <circle cx="12" cy="12" r="10"/>
    <path d="m4.93 4.93 4.24 4.24"/>
    <path d="m14.83 9.17 4.24-4.24"/>
    <path d="m14.83 14.83 4.24 4.24"/>
    <path d="m9.17 14.83-4.24 4.24"/>
    <circle cx="12" cy="12" r="4"/>
  </svg>
);

// Zap Icon Component for the How to Connect link
const ZapIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>
  </svg>
);

const Aside = () => {
  const { toggleNav } = useNavigation();
  const [expandedGatewayIds, setExpandedGatewayIds] = useState<Set<string>>(new Set());
  const matchRoute = useMatchRoute();
  const { gateways } = useGateways();
  const { wallet } = useWalletContext();
  
  // Check if user is in guest mode
  const isGuestMode = useMemo(() => WalletTypeDetector.isGuestWallet(wallet), [wallet]);

  useEffect(() => {
    if (!gateways) return;

    // Find gateway with active sub-route and ensure it stays expanded
    const activeGatewayId = gateways.find(gateway => {
      const isOverviewActive = matchRoute({ to: `/gateway/${gateway.id}`, fuzzy: false });
      const isLocationActive = matchRoute({ to: `/gateway/${gateway.id}/location`, fuzzy: false });
      const isDevicesActive = matchRoute({ to: `/gateway/${gateway.id}/devices`, fuzzy: false });
      return isOverviewActive || isLocationActive || isDevicesActive;
    })?.id;

    if (activeGatewayId) {
      setExpandedGatewayIds(new Set([activeGatewayId]));
    }
  }, [matchRoute, gateways]);

  const handleMainNavClick = () => {
    if (window.innerWidth < 1024) toggleNav();
  };

  const ExternalLink = ({ href, icon: Icon, children }: { href: string; icon: any; children: React.ReactNode }) => (
    <a 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between rounded-lg p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#292929]/50"
    >
      <div className="flex items-center">
        <Icon className="h-5 w-5 mr-2 text-[#00FF84]" />
        <span>{children}</span>
      </div>
      <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
    </a>
  );

  return (
    <>
      <style>
        {`
          /* Hide scrollbar for all screen sizes while maintaining functionality */
          .nav-sidebar::-webkit-scrollbar {
            display: none;
          }
          .nav-sidebar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      <nav className="nav-sidebar h-full overflow-y-auto px-4 pt-16 pb-6 lg:pt-4 bg-[#1A1A1A] dark:bg-[#1A1A1A] light:bg-white">
        <div className="flex flex-col space-y-2 mb-6">
          <NavItem 
            title="Home" 
            path="/" 
            icon={<HomeIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          <div className="lg:hidden">
            <WalletConnect variant="sidebar" />
          </div>
          <div className="border-t border-[#292929] dark:border-[#292929] light:border-gray-200 pt-6 lg:hidden">
            <h2 className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400 light:text-gray-500 font-polysans">
              Quick Actions
            </h2>
            <NavItem 
              title="Rewards" 
              path="/rewards" 
              icon={<BoltIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
              onClick={handleMainNavClick}
            />
            <NavItem 
              title="Leaderboard" 
              path="/leaderboard" 
              icon={<CrownIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
              onClick={handleMainNavClick}
            />
            <NavItem 
              title="Notifications" 
              path="/notifications" 
              icon={<BellIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
              onClick={handleMainNavClick}
            />
            {isGuestMode ? (
              <>
                <div
                  className="flex items-center px-2 py-2 text-gray-500 cursor-not-allowed"
                  title="Not available in Guest Mode"
                >
                  <PlusCpuIcon className="h-5 w-5 mr-2 text-gray-500" />
                  <span>Add Hardware Gateway</span>
                </div>
                <div
                  className="flex items-center px-2 py-2 text-gray-500 cursor-not-allowed"
                  title="Not available in Guest Mode"
                >
                  <CloudIcon className="h-5 w-5 mr-2 text-gray-500" />
                  <span>Add Cloud Gateway</span>
                </div>
              </>
            ) : (
              <>
                <NavItem 
                  title="Add Hardware Gateway" 
                  path="/onboarding-wizard" 
                  icon={<PlusCpuIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
                  onClick={handleMainNavClick}
                />
                <NavItem 
                  title="Add Cloud Gateway" 
                  path="/onboarding/cloud" 
                  icon={<CloudIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
                  onClick={handleMainNavClick}
                />
              </>
            )}
          </div>
          <div className="border-t border-[#292929] pt-6">
            <GatewayList 
              onNavigate={handleMainNavClick}
              expandedGatewayIds={expandedGatewayIds}
              setExpandedGatewayIds={setExpandedGatewayIds}
            />
          </div>
        </div>

        <div className="border-t border-[#292929] dark:border-[#292929] light:border-gray-200 pt-6 mt-6">
          <h2 className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400 light:text-gray-500 font-polysans">
            {isGuestMode ? 'Data' : 'Your Data'}
          </h2>
          <NavItem
            title="Meter Data"
            path="/data/meter"
            icon={<MeterIcon className="h-5 w-5 mr-2 text-[#00FF84] dark:text-[#00FF84] light:text-[#00FF84]" state="active" />}
            onClick={handleMainNavClick}
          />
          <NavItem
            title="Solar Data"
            path="/data/solar"
            icon={<SolarIcon className="h-5 w-5 mr-2 text-[#00FF84] dark:text-[#00FF84] light:text-[#00FF84]" state="active" />}
            onClick={handleMainNavClick}
          />
          <NavItem
            title="Battery Data"
            path="/data/battery"
            icon={<BatteryIcon className="h-5 w-5 mr-2 text-[#00FF84] dark:text-[#00FF84] light:text-[#00FF84]" state="active" />}
            onClick={handleMainNavClick}
          />
        </div>

        <div className="border-t border-[#292929] dark:border-[#292929] light:border-gray-200 pt-6 mt-6">
          <h2 className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400 light:text-gray-500 font-polysans">
            Tools
          </h2>
          <NavItem 
            title="Gateway Information" 
            path="/connect-gw" 
            icon={<InformationCircleIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          <NavItem 
            title="Gateway Status Check" 
            path="/gateway-status" 
            icon={<WrenchScrewdriverIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          <NavItem 
            title="Gateway Location" 
            path="/gateway-location-tool" 
            icon={<MapPinIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          {isFeatureEnabled('ENABLE_RECOVER_WALLETLESS') && (
            <NavItem 
              title="Recover Wallet" 
              path="/recover-wallet" 
              icon={<KeyIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
              onClick={handleMainNavClick}
            />
          )}
        </div>

        <div className="border-t border-[#292929] dark:border-[#292929] light:border-gray-200 pt-6 mt-6">
          <h2 className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400 light:text-gray-500 font-polysans">
            Links
          </h2>
          <ExternalLink href="https://sourceful.energy/connect" icon={ZapIcon}>
            Buy a Gateway or Zap
          </ExternalLink>
          <ExternalLink href="https://app.sourceful.energy" icon={FaMobileAlt}>
            Download App
          </ExternalLink>
          <ExternalLink href="https://explorer.sourceful.energy" icon={GlobeIcon}>
            Explorer
          </ExternalLink>
          <ExternalLink href="https://discord.com/invite/srcful" icon={FaDiscord}>
            Discord
          </ExternalLink>
          <ExternalLink href="https://docs.sourceful.energy" icon={FaBook}>
            Documentation
          </ExternalLink>
          <ExternalLink href="https://support.sourceful.energy" icon={LifeBuoyIcon}>
            Support
          </ExternalLink>
        </div>

        <div className="border-t border-[#292929] dark:border-[#292929] light:border-gray-200 pt-6 mt-6">
          <h2 className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400 light:text-gray-500 font-polysans">
            Developer
          </h2>
          <NavItem 
            title="Debug" 
            path="/debug" 
            icon={<BeakerIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          <NavItem 
            title="Alt Onboarding" 
            path="/alt-onboarding-wizard" 
            icon={<RocketLaunchIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          <NavItem 
            title="Zap Onboarding" 
            path="/zap-onboarding-wizard" 
            icon={<ZapIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          <NavItem 
            title="Echo" 
            path="/echo" 
            icon={<ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
          <NavItem 
            title="Deep Thought" 
            path="/deep-thought" 
            icon={<SparklesIcon className="h-5 w-5 mr-2 text-[#00FF84]" />}
            onClick={handleMainNavClick}
          />
        </div>
      </nav>
    </>
  );
};

export default Aside;
